import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import _ from 'underscore';
import { titleize } from 'underscore.string';
import validator from 'email-validator';
import lodash from 'lodash';
import { withTranslation } from 'react-i18next';
import formatMoney from 'utils/formatters/Money';
import { getCurrencySymbol } from 'components/modules/master/formatters';
import SendDOEmailBulk from './SendDOEmailBulk';
import { getDeliveryOrderRecipients } from '../../../helpers/shipments';

const defaultColumnHeadersDO = [
  { localizationSlug: 'SHIPMENT', value: 'Shipment', titleize: true },
  { localizationSlug: 'SHIPPER', value: 'Shipper', titleize: true },
  { localizationSlug: 'EMAIL ', value: 'Email', titleize: true },
  { localizationSlug: 'APPLY_TO_ALL', value: 'Apply To All', titleize: false },
  { localizationSlug: 'REMOVE ', value: 'Remove', titleize: true },
];
const defaultColumnHeaders = [
  { localizationSlug: 'SHIPMENT', value: 'Shipment', titleize: true },
  { localizationSlug: 'SHIPPER', value: 'Shipper', titleize: true },
  { localizationSlug: 'EQUIPMENT', value: 'Equipment', titleize: true },
  {
    localizationSlug: 'CURRENT_CARRIER_COST_CURRENCY',
    value: 'Current Carrier Cost ($)',
    titleize: false,
  },
  {
    localizationSlug: 'NEW_CARRIER_COST_CURRENCY',
    value: 'New Carrier Cost ($)',
    titleize: false,
  },
  {
    localizationSlug: 'ADJUSTMENT_REASON',
    value: 'Adjustment Reason',
    titleize: false,
  },
  { localizationSlug: 'APPLY_TO_ALL', value: 'Apply To All', titleize: false },
  { localizationSlug: 'REMOVE ', value: 'Remove', titleize: true },
];

class CarrierGroup extends Component {
  constructor(props) {
    super(props);
    this.state = {
      error: false,
      decimalError: false,
      carrierCost: null,
    };
  }

  getHeaders() {
    const { t } = this.props;
    return this.props.send_do
      ? defaultColumnHeadersDO.map((item) => t(item))
      : defaultColumnHeaders.map((item) => t(item));
  }

  getDefaultForm = (shipment) => {
    const { _id: id, carrier } = shipment;

    const { shipmentUpdates } = this.props;

    if (shipmentUpdates[id] && shipmentUpdates[id].meta) {
      const email = shipmentUpdates[id].meta.email || null;
      if (email) {
        return email;
      }
    }
    return getDeliveryOrderRecipients(carrier) || '';
  };

  handleApplyToAll = (shipmentId) => () => this.props.onApplyToAll(shipmentId);

  handleSaveAll = () => {
    if (!this.usingValidDecimals(this.state.carrierCost)) {
      return this.setState({ decimalError: true });
    }
    this.props.onSaveAll();
  };

  usingValidDecimals(cost) {
    if (!cost) return true;
    const charArray = cost.toString().split('');

    if (charArray.indexOf('.') > -1) {
      const index = charArray.indexOf('.');
      if (charArray[index + 3]) {
        return false;
      }
      return true;
    }
    return true;
  }

  handleCarrierChange = (shipmentId, field) => (e) => {
    const { shipmentUpdates } = this.props;

    const {
      target: { value },
    } = e;

    if (field === 'carrier_cost') {
      let carrierCost = null;

      if (parseFloat(value)) {
        carrierCost = parseFloat(value);
        this.setState({ carrierCost, decimalError: false });
      }

      this.props.onCarrierCostChange(shipmentId, {
        carrierCost,
        reason: shipmentUpdates[shipmentId].meta.reason,
      });
    } else {
      this.props.onCarrierCostChange(shipmentId, {
        carrierCost: shipmentUpdates[shipmentId].meta.carrier_cost,
        reason: value,
      });
    }
  };

  handleRemoveShipment = (shipmentId) => () => {
    const {
      carrierGroup: { id },
    } = this.props;
    this.props.onRemoveShipment(id, shipmentId);
  };

  handleEquipmentChange = (shipmentId) => (e) => {
    const {
      target: { value },
    } = e;
    this.props.onEquipmentChange(shipmentId, value);
  };

  renderHeader() {
    const { t } = this.props;
    const columnHeaders = this.getHeaders();
    return (
      <thead>
        <tr>
          {columnHeaders.map((col) => (
            <th
              key={col.value}
              className="leading-none tracking-wide font-medium text-gray-500 px-6 py-3 border-b bg-gray-50 border-gray-200 border-solid text-left text-xs uppercase"
            >
              {col.titleize
                ? titleize(t(col.localizationSlug))
                : t(col.localizationSlug)}
            </th>
          ))}
        </tr>
      </thead>
    );
  }

  renderColumnsDO = (shipment) => {
    const { _id: id } = shipment;

    const idKey = `id_${id}`;
    const shipperNameKey = `shipper_${id}`;
    const shipmentLink = `/shipments/${id}`;
    const emailKey = `/emails_/${id}`;

    return [
      <td
        className="px-6 py-4 text-sm border-b border-grey-lighter border-solid text-gray-500"
        key={idKey}
      >
        <a href={shipmentLink} target="_blank" rel="noreferrer">
          {shipment.shipment_id}
        </a>
      </td>,
      <td
        className="px-6 py-4 text-sm border-b border-grey-lighter border-solid text-gray-500"
        key={shipperNameKey}
        style={{ minWidth: '150px', maxWidth: '150px' }}
      >
        {shipment.shipper_name}
      </td>,
      <td
        key={emailKey}
        className="px-6 py-4 whitespace-nowrap border-b border-gray-200 border-solid text-gray-500"
        style={{ minWidth: '200px', maxWidth: '200px' }}
      >
        {' '}
        <SendDOEmailBulk
          error={this.state.error || null}
          shipment={shipment}
          updates={this.props.shipmentUpdates}
          emailKey={id}
          updateEmail={this.props.onEmailChange}
          carrier={shipment.carrier}
          defaultRecipient={this.getDefaultForm(shipment)}
          onSend={this.sendBulkEmails}
        />
      </td>,
      this.renderApplyToAllButton(shipment, true),
      this.renderRemoveShipmentButton(shipment),
    ];
  };

  renderEquipmentsColumn(shipment) {
    const { equipments } = shipment;
    const { t } = this.props;
    if (equipments.length < 1) {
      return formatEquipments(equipments, t);
    }

    const { shipmentUpdates } = this.props;
    const {
      meta: { equipment: selectedEquipment },
    } = shipmentUpdates[shipment._id];
    return (
      <select
        className="form-select"
        onChange={this.handleEquipmentChange(shipment._id)}
        value={selectedEquipment}
      >
        <option value="">{t('SELECT_AN_EQUIPMENT')}</option>
        {equipments.map((equipment) => (
          <option key={equipment._id} value={equipment._id}>
            {equipment.equipment_id}
          </option>
        ))}
      </select>
    );
  }

  renderColumns(shipment) {
    const { t } = this.props;
    const { _id } = shipment;

    const { shipmentUpdates } = this.props;

    const carrierCost = shipmentUpdates[_id].meta.carrier_cost;
    const carrierReason = shipmentUpdates[_id].meta.reason;
    const id = _id;
    const carrierCostKey = `carrier_cost_${_id}`;
    const adjustmentReason = `adjustment_reason_${_id}`;
    const idKey = `id_${id}`;
    const shipperNameKey = `shipper_${id}`;
    const equipmentsKey = `equipments_${id}`;
    const shipmentLink = `/shipments/${id}`;

    const currentCostKey = `current_carrier_cost_${id}`;
    return [
      <td
        className="px-6 py-4 text-sm border-b border-grey-lighter border-solid text-gray-500"
        key={idKey}
      >
        <a href={shipmentLink} target="_blank" rel="noreferrer">
          {shipment.shipment_id}
        </a>
      </td>,
      <td
        className="px-6 py-4 text-sm border-b border-grey-lighter border-solid text-gray-500"
        key={shipperNameKey}
      >
        {shipment.shipper_name}
      </td>,
      <td
        className="px-6 py-4 text-sm border-b border-grey-lighter border-solid text-gray-500"
        key={equipmentsKey}
      >
        {this.renderEquipmentsColumn(shipment)}
      </td>,
      <td
        className="px-6 py-4 text-sm border-b border-grey-lighter border-solid text-gray-500"
        key={currentCostKey}
      >
        {formatMoney(shipment.carrier_cost, shipment?.currency)}
      </td>,
      <td
        key={carrierCostKey}
        className="px-6 py-4 whitespace-nowrap border-b border-gray-200 border-solid text-gray-500"
      >
        <input
          type="number"
          onChange={this.handleCarrierChange(shipment._id, 'carrier_cost')}
          className="form-field"
          placeholder={t('CARRIER_COST_WITH_CURRENCY', {
            currency: getCurrencySymbol(shipment?.currency),
          })}
          value={carrierCost}
        />
        {this.state.decimalError && (
          <p style={{ marginTop: '5px', fontSize: '10px', color: 'red' }}>
            {t('USE_TWO_DECIMAL_PLACES')}
          </p>
        )}
      </td>,
      <td
        className="px-6 py-4 text-sm border-b border-grey-lighter border-solid text-gray-500"
        key={adjustmentReason}
      >
        <input
          type="text"
          onChange={this.handleCarrierChange(shipment._id, 'reason')}
          className="form-field"
          placeholder={t('ADJUSTMENT_REASON')}
          value={carrierReason}
        />
      </td>,
      this.renderApplyToAllButton(shipment),
      this.renderRemoveShipmentButton(shipment),
    ];
  }

  renderApplyToAllButton(shipment) {
    const { t } = this.props;
    const applyAllKey = `apply_${shipment._id}`;
    const buttonClassName = {
      button: true,
      'button-hollow-green': true,
    };
    var disableApplyAll = false;
    if (this.props.disableSaves) {
      disableApplyAll = this.props.disableSaves.includes(shipment._id);
    }
    return (
      <td
        className="px-6 py-4 whitespace-nowrap font-medium border-b border-grey-lighter border-solid"
        key={applyAllKey}
      >
        <button
          type="button"
          disabled={disableApplyAll || this.props.saving}
          className={classnames(buttonClassName)}
          onClick={this.handleApplyToAll(shipment._id)}
        >
          {titleize(t('APPLY'))}
        </button>
      </td>
    );
  }

  renderRemoveShipmentButton(shipment) {
    const removeShipmentKey = `remove_${shipment._id}`;
    return (
      <td
        className="px-6 py-4 whitespace-nowrap font-medium border-b border-grey-lighter border-solid"
        key={removeShipmentKey}
      >
        <button
          className="button text-grey-dark hover:text-grey-darker"
          onClick={this.handleRemoveShipment(shipment._id)}
        >
          <i className="fa fa-remove" />
        </button>
      </td>
    );
  }

  renderBody() {
    const {
      carrierGroup: { shipments },
      send_do,
    } = this.props;
    return (
      <tbody>
        {shipments.map((shipment) => (
          <tr key={shipment._id}>
            {send_do
              ? this.renderColumnsDO(shipment)
              : this.renderColumns(shipment)}
          </tr>
        ))}
      </tbody>
    );
  }

  render() {
    const {
      carrierGroup: { name, id },
      send_do,
      saving,
      disableSave,
      t,
    } = this.props;

    const buttonDisabled = saving || disableSave;
    const carrierLink = `/users/${id}`;
    const disabledSaves = !!(
      this.props.disableSaves && this.props.disableSaves.length
    );
    console.log('Disable saves ', disabledSaves);
    return (
      <div className="shadow align-middle inline-block min-w-full overflow-hidden rounded border-b border-grey-lighter bg-white">
        <div className="flex justify-between px-4 py-2">
          <h4 className="my-2 text-xl text-grey-darker">
            <a href={carrierLink} target="_blank" rel="noopener noreferrer">
              {name}
            </a>
          </h4>
        </div>
        <div className="shipment-rows">
          <table className="min-w-full">
            {this.renderHeader()}
            {this.renderBody()}
          </table>
          {!send_do && (
            <div className="flex justify-end items-baseline p-4">
              <button
                className="button button-green flex items-center"
                disabled={disabledSaves}
                onClick={this.handleSaveAll}
              >
                {saving && (
                  <div className="flex items-center mr-2">
                    <i className="fa fa-spinner fa-spin" />
                  </div>
                )}
                <span>{t('SAVE_ALL')}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }
}

CarrierGroup.propTypes = {
  carrierGroup: PropTypes.shape({
    name: PropTypes.string,
    id: PropTypes.string,
    shipments: PropTypes.arrayOf(
      PropTypes.shape({
        _id: PropTypes.string,
        shipment_id: PropTypes.string,
        carrier_cost: PropTypes.number,
      }),
    ),
  }).isRequired,
  shipmentUpdates: PropTypes.object,
  disableSave: PropTypes.bool,
  saving: PropTypes.bool,
  onApplyToAll: PropTypes.func,
  onEquipmentChange: PropTypes.func,
  onRemoveShipment: PropTypes.func,
  onEmailChange: PropTypes.func,
  onCarrierCostChange: PropTypes.func,
  send_do: PropTypes.bool,
  onSaveAll: PropTypes.func.isRequired,
};

function formatEquipments(equipments, t) {
  if (equipments.length === 0) {
    return t('NONE');
  }

  return (
    <ul>
      {equipments.map((e) => (
        <li className="mb-2" key={e._id}>
          {e.equipment_id}
        </li>
      ))}
    </ul>
  );
}

export default withTranslation()(CarrierGroup);
